import {ServiceOrder} from '../models/service-order';
import {Flow} from '../models/flow';
import {finalActiveEvent} from './flow-utils';

/**
 * Utilidades para trabajar con ServiceOrder
 */

/**
 * Verifica si una ServiceOrder está finalizada comparando su estado actual
 * con el estado final del flujo (el evento con la secuencia más alta).
 *
 * @param serviceOrder La ServiceOrder a verificar
 * @param flow El Flow que contiene los eventos del flujo (opcional, por defecto usa serviceOrder.flow)
 * @returns true si la ServiceOrder está en el estado final, false en caso contrario
 *
 * @example
 * ```typescript
 * // Usando el flow de la ServiceOrder
 * const isCompleted = isServiceOrderCompleted(serviceOrder);
 *
 * // Pasando un flow específico
 * const isCompleted = isServiceOrderCompleted(serviceOrder, flow);
 *
 * if (isCompleted) {
 *   // La orden está finalizada
 * }
 * ```
 */
export function isServiceOrderCompleted(serviceOrder: ServiceOrder, flow?: Flow): boolean {
  // Validaciones básicas
  if (!serviceOrder) {
    return false;
  }

  if (!serviceOrder.state) {
    return false;
  }

  // Usar el flow proporcionado o el flow de la ServiceOrder por defecto
  const flowToUse = flow || serviceOrder.flow;

  if (!flowToUse) {
    return false;
  }

  if (!flowToUse.events || flowToUse.events.length === 0) {
    return false;
  }

  // El estado final es la etapa vigente de mayor secuencia. Las etapas retiradas
  // (isActive === false) sobreviven solo para resolver historial y no pueden ser
  // el final del flujo.
  const finalState = finalActiveEvent(flowToUse.events);

  if (!finalState) {
    return false;
  }

  // Comparar el estado actual de la ServiceOrder con el ID del estado final
  const isCompleted = serviceOrder.state === finalState.id.toString();

  return isCompleted;
}

